import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_ITEMS, MODULES } from '@/components/Toolbar/constants';
import type { RootDispatch, RootState } from '@/components/WellLoggingOperations/types';
import type { Item, ItemNode } from '@/components/WellLoggingOperations/types/tree';
import { ROUTES } from '@/lib/routes';
import type { Item as DropdownItem } from '@/shared/DropDownWrapper/types';

export const delay = (milliseconds = 0): Promise<void> => new Promise(resolve => setTimeout(resolve, milliseconds));

type NormalizedData<T> = {
    ids: string[];
    data: Record<string, T>;
};
type ListItem = { key: string; isGroup: boolean; isSummaryMode?: boolean; isWellMode?: boolean };

export const asyncNormalizeData = async <T extends Record<string, any>>(
    dataArray: T[],
    idName: keyof T = 'id',
): Promise<NormalizedData<T>> => {
    const normalizedObject: NormalizedData<T> = {
        ids: [],
        data: {},
    };

    for (const object of dataArray) {
        await delay();
        const id = String(object[idName]);
        normalizedObject.ids.push(id);
        normalizedObject.data[id] = idName !== 'id' ? { ...object, id } : object;
    }
    return normalizedObject;
};

export const normalizeData = <T extends Record<string, any>>(
    dataArray: T[],
    idName: keyof T = 'id',
): NormalizedData<T> => {
    const normalizedObject: NormalizedData<T> = {
        ids: [],
        data: {},
    };

    for (const object of dataArray) {
        const id = String(object[idName]);
        normalizedObject.ids.push(id);
        normalizedObject.data[id] = idName !== 'id' ? { ...object, id } : object;
    }
    return normalizedObject;
};

export const swapElements = <T>(array: T[], indexFirst: number, indexSecond: number): T[] => {
    const copyArray = [...array];
    copyArray[indexFirst] = copyArray.splice(indexSecond, 1, copyArray[indexFirst])[0];
    return copyArray;
};

export const filterObjectByKeys = <T extends Record<string, any>>(
    obj: T,
    keys: Array<keyof T>,
): Partial<Record<keyof T, T[keyof T] | null>> => {
    return keys.reduce(
        (acc, key) => {
            acc[key] = obj[key] ?? null;
            return acc;
        },
        {} as Partial<Record<keyof T, T[keyof T] | null>>,
    );
};

export const isSubObject = <T extends Record<string, any>>(targetObj: T, obj: T): boolean => {
    for (const key in targetObj) {
        if (!(key in obj) || (targetObj[key] !== obj[key] && !key.startsWith('group'))) {
            return false;
        }
    }
    return true;
};

export const conditionallyArray = <T>(obj: T | T[], condition: boolean): T[] => {
    return condition ? (Array.isArray(obj) ? obj : [obj]) : [];
};

const getPath = (module: { id: number; path: string; relatedPaths: string[] }, miningType?: MiningType) =>
    module.id === 1 ? module.relatedPaths[miningType === 'single' ? 0 : 1] : module.path;

//возвращает функцию, которая открывает модуль для сущности в новой вкладке
//работает с сущностями из списка по ключу key, либо с сущностями Информационного дерева item
export const useOpenNewTab = (listItem?: ListItem) => {
    const dispatch = useDispatch() as unknown as RootDispatch;
    const { addItem } = dispatch.lists;
    const { setCalculationObject, addToListsAsGroup, deleteProfile } = dispatch.tree;
    const listsData = useSelector((state: RootState) => state.lists.data);
    const dataItem = listsData?.filter(data => data.key === listItem?.key)[0];

    return async (
        moduleId: number,
        item?: Item | null,
        tabletKey?: string,
        miningType?: MiningType,
        createTabletKey = false,
    ): Promise<void> => {
        let createdTabletKey: string | undefined;
        const mod = CONTEXT_MODULES.find(({ id }) => moduleId === id);

        if (
            (createTabletKey && item) ||
            (MODULES_NEED_NEW_TABLE.includes(moduleId) && item?.node === 'well')
            // ||
            // item?.node === 'shaft'
        ) {
            createdTabletKey = await addItem(item);
        }
        if (mod && mod.title === 'Характеристики вытеснения' && listItem?.isGroup) {
            if (dataItem && dataItem.children.length > 3) localStorage.setItem('sharedItem', JSON.stringify(dataItem));
        } else {
            if (item && item.node === 'shaft') {
                item = { ...item, modTitle: mod.title };
                addToListsAsGroup(item);
                // if (miningType !== 'summary') setCalculationObject(item);
            }
            if (createdTabletKey) {
                localStorage.setItem('sharedKey', createdTabletKey);
            } else if (tabletKey) {
                if (listItem?.isGroup && miningType !== 'summary')
                    localStorage.setItem('sharedItem', JSON.stringify(dataItem));
                else localStorage.setItem('sharedKey', tabletKey);
            } else if (item) {
                localStorage.setItem('sharedItem', JSON.stringify(item));
            } else {
                console.warn('Не указан tabletId либо item');
            }
        }

        if (mod) {
            window.open(`${window.location.origin}/${getPath(mod, miningType)}`, '_blank', 'noopener,noreferrer');
        }
    };
};

//возвращает функцию, которая открывает модуль для сущности в текущей вкладке
//работает с сущностями из списка по ключу key, либо с сущностями Информационного дерева item

export const useOpenCurrentTab = (item?: Item, listItem?: ListItem) => {
    const router = useRouter();
    const dispatch = useDispatch() as unknown as RootDispatch;
    const { setCalculationObject, addToListsAsGroup, deleteProfile } = dispatch.tree;
    const selectItem = dispatch.lists.selectItem;
    const { addItem } = dispatch.lists;

    const listsData = useSelector((state: RootState) => state.lists.data);
    return async (
        moduleId: number,
        tabletKey?: string,
        miningType?: MiningType,
        createTabletKey = false,
    ): Promise<void> => {
        const mod = CONTEXT_MODULES.find(({ id }) => moduleId === id);

        if (mod) {
            const dataItem = listsData?.filter(data => data.key === listItem?.key)[0];

            if (mod.title === 'Добыча/закачка' && !listItem?.isGroup && miningType !== 'summary') {
                // setCalculationObject(null);
                dispatch.miningAndPumping.setMiniMapWidgetVisible(false);
            }
            await router.push(getPath(mod, miningType), getPath(mod, miningType), { shallow: true });
            let createdTabletKey: string | undefined;

            if (mod.title === 'Характеристики вытеснения' && listItem?.isGroup) {
                if (dataItem && dataItem?.children && dataItem?.children.length > 3) setCalculationObject(dataItem);
            } else {
                if (
                    (createTabletKey && item) ||
                    (MODULES_NEED_NEW_TABLE.includes(moduleId) && item?.node === 'well')
                    //  ||
                    // item?.node === 'shaft'
                ) {
                    createdTabletKey = await addItem(item);
                }
                if (item && item.node === 'shaft') {
                    item = { ...item, modTitle: mod.title };
                    addToListsAsGroup(item);
                    // if (miningType !== 'summary') setCalculationObject(item);
                }
                if (createdTabletKey) {
                    if (createdTabletKey && item) selectItem(createdTabletKey);
                } else if (tabletKey) {
                    if (listItem?.isGroup && miningType !== 'summary') setCalculationObject(dataItem ?? null);
                    else {
                        selectItem(tabletKey);
                    }
                } else if (item || (mod.title === 'Добыча/закачка' && listItem && listItem?.isGroup)) {
                    let currentItem = item;
                    if (mod.title === 'Добыча/закачка' && listItem && listItem?.isGroup && dataItem)
                        currentItem = dataItem;

                    if (currentItem) {
                        setCalculationObject(currentItem);
                    } else {
                        setCalculationObject(null);
                    }
                } else {
                    console.warn('Не указан tabletId либо item');
                }
            }
        }
    };
};

const getDropdownModuleItem = (
    title: string,
    itemKey: string,
    openNewTab: () => Promise<void>,
    openCurrentTab: () => Promise<void>,
    closeDropdown: () => void,
): DropdownItem => {
    return {
        title,
        type: undefined,
        itemKey: itemKey,
        children: [
            {
                type: undefined,
                itemKey: `${itemKey}-2`,
                title: 'Открыть в текущей вкладке',
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick: async () => {
                    await openCurrentTab();
                    closeDropdown();
                },
            },
            {
                type: undefined,
                itemKey: `${itemKey}-1`,
                title: 'Открыть в новой вкладке',
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick: async () => {
                    await openNewTab();
                    closeDropdown();
                },
            },
        ],
    };
};

type MiningType = 'single' | 'summary';

// Возвращает схему контекстного меню для DropdownWrapper
// Работает с 2 видами сущностей, если на вход подается key, это сущность из списка, и key это ее ключ
// item для Информационного дерева, любая сущность обрабатывается хуком и на основании item.node генериерет контекстное меню

export const useContextMenuItems = (
    listItem?: { key: string; isGroup: boolean; isWellMode: boolean; isSummaryMode: boolean },
    item?: Item,
    closeDropdown?: () => void,
): DropdownItem[] => {
    const openCurrentTab = useOpenCurrentTab(item, listItem);
    const openNewTab = useOpenNewTab(listItem);
    const listsData = useSelector((state: RootState) => state.lists.data);
    const dataItem = listsData?.filter(data => data.key === listItem?.key)[0];
    let key: string | undefined;
    let modules: number[] = [];
    if (item?.node === 'shaft') console.log('useContextMenuItems', [item, key, modules]);
    if (item) {
        modules = (item?.node && NODE_TYPE_MODULES[item?.node]) ?? [];
    } else if (listItem) {
        const { isGroup } = listItem;
        key = listItem.key;
        modules = NODE_TYPE_MODULES.well ?? [];
        if (isGroup) {
            modules.push(11);
            modules.push(9);
            if (dataItem && dataItem?.children && dataItem?.children?.length > 3) {
                modules.push(6);
            }

            if (dataItem && dataItem?.children && dataItem?.children?.length <= 3 && modules.includes(6)) {
                modules = modules.filter(id => id !== 6);
            }
        }
        if (!isGroup) {
            modules = modules.filter(id => id !== 6 && id !== 11);
        }
    }

    const items: DropdownItem[] = [];
    const filteredModules = CONTEXT_MODULES.filter(({ id }) => modules.includes(id));

    const currentItem = item ? item : dataItem;
    for (const { title, id } of filteredModules) {
        if (id === 1 && currentItem?.node) {
            const needSingle = key || MINING_MODE[currentItem.node]?.includes('single');
            const needSummary =
                (key && currentItem.node !== 'well') || MINING_MODE[currentItem.node]?.includes('summary');

            items.push(
                ...conditionallyArray(
                    getDropdownModuleItem(
                        title,
                        `${id.toString()}-single`,
                        () => openNewTab(id, currentItem, key, 'single', currentItem?.node === 'well'),
                        () => openCurrentTab(id, key, 'single', currentItem?.node === 'well'),
                        closeDropdown,
                    ),
                    !!needSingle,
                ),
                ...conditionallyArray(
                    getDropdownModuleItem(
                        `${title} (суммарный режим)`,
                        `${id.toString()}-summary`,
                        () => openNewTab(id, currentItem, key, 'summary', currentItem?.node === 'well'),
                        () => openCurrentTab(id, key, 'summary', currentItem?.node === 'well'),
                        closeDropdown,
                    ),
                    !!needSummary || (listItem && listItem?.isGroup),
                ),
            );
        } else {
            const openModuleNewTab = () => openNewTab(id, item, key, listItem?.isGroup ? 'summary' : 'single');
            const openModuleCurrentTab = () => openCurrentTab(id, key, listItem?.isGroup ? 'summary' : 'single');
            // const openModuleCurrentTab = () => openCurrentTab(id, key, 'single');
            items.push(
                getDropdownModuleItem(title, id.toString(), openModuleNewTab, openModuleCurrentTab, closeDropdown),
            );
        }
    }

    return items;
};

export const useHotkeyLogger = (keysArr?: string[], callback?: () => void) => {
    useEffect(() => {
        if (!keysArr || !callback) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            const keys = keysArr.map(key => key.toLowerCase());
            const ctrl = keys.includes('ctrl') ? event.ctrlKey : true;
            const shift = keys.includes('shift') ? event.shiftKey : true;
            const alt = keys.includes('alt') ? event.altKey : true;
            const meta = keys.includes('meta') ? event.metaKey : true; // Для Mac ⌘ (Command)
            const key = keys.includes(event.key.toLowerCase());

            if (ctrl && shift && alt && meta && key) {
                event.preventDefault();
                callback();
            }
        };

        document.addEventListener('keydown', handleKeyDown, { passive: false });
        //Стоит убедиться что компонент размонтируется чтобы событие удалялось
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [callback, keysArr]);
};

export const REPORT_MODULE = {
    id: 0,
    title: 'Отчетность',
    keyName: 'R',
    path: ROUTES.REPORTS,
    relatedPaths: [ROUTES.REPORTS],
};

const CONTEXT_MODULES = [...BUTTON_ITEMS, REPORT_MODULE];

const NODE_TYPE_MODULES: Partial<Record<ItemNode, number[]>> = {
    organization: [
        MODULES.REPORTS,
        MODULES.TECHNOLOGICAL_EFFICIENCY_GTM,
        MODULES.DESATURATION_CHARACTERISTICS,
        MODULES.MINING_AND_PUMPING,
    ],
    area: [MODULES.MAP_GENERATION, MODULES.GSR, MODULES.FACTOR_ANALYSIS, MODULES.REPORTS],
    field: [
        MODULES.MINING_AND_PUMPING,
        MODULES.TECHNOLOGICAL_EFFICIENCY_GTM,
        MODULES.MAP_GENERATION,
        MODULES.OIL_PRODUCTION_LOSS,
        MODULES.DESATURATION_CHARACTERISTICS,
        MODULES.GSR,
        MODULES.FACTOR_ANALYSIS,
        MODULES.REPORTS,
    ],
    license: [MODULES.MAP_GENERATION, MODULES.GSR, MODULES.FACTOR_ANALYSIS, MODULES.REPORTS],
    develop: [
        MODULES.MINING_AND_PUMPING,
        MODULES.MAP_GENERATION,
        MODULES.OIL_PRODUCTION_LOSS,
        MODULES.DESATURATION_CHARACTERISTICS,
        MODULES.REPORTS,
    ],
    pad: [
        MODULES.MINING_AND_PUMPING,
        MODULES.TECHNOLOGICAL_EFFICIENCY_GTM,
        MODULES.MAP_GENERATION,
        MODULES.DESATURATION_CHARACTERISTICS,
        MODULES.REPORTS,
    ],
    elevation: [
        MODULES.MINING_AND_PUMPING,
        MODULES.TECHNOLOGICAL_EFFICIENCY_GTM,
        MODULES.MAP_GENERATION,
        MODULES.OIL_PRODUCTION_LOSS,
        MODULES.DESATURATION_CHARACTERISTICS,
        MODULES.GSR,
        MODULES.REPORTS,
    ],
    industrial: [MODULES.MAP_GENERATION, 0],
    company: [
        MODULES.MINING_AND_PUMPING,
        MODULES.TECHNOLOGICAL_EFFICIENCY_GTM,
        MODULES.MAP_GENERATION,
        MODULES.DESATURATION_CHARACTERISTICS,
        MODULES.FACTOR_ANALYSIS,
        MODULES.REPORTS,
    ],
    group: [
        MODULES.MINING_AND_PUMPING,
        MODULES.MAP_GENERATION,
        MODULES.OIL_PRODUCTION_LOSS,
        MODULES.DESATURATION_CHARACTERISTICS,
    ],
    well: [MODULES.REPORTS, MODULES.MINING_AND_PUMPING, MODULES.WELL_LOGGING_OPERATIONS, MODULES.WELLS_VIEW],
    shaft: [MODULES.REPORTS, MODULES.MINING_AND_PUMPING, MODULES.WELL_LOGGING_OPERATIONS, MODULES.WELLS_VIEW],

    kns: [MODULES.MINING_AND_PUMPING],
};

const MODULES_NEED_NEW_TABLE = [0, 3, 7];

const MINING_MODE: Partial<Record<ItemNode, Array<'single' | 'summary'>>> = {
    organization: ['summary'],
    field: ['single', 'summary'],
    develop: ['single', 'summary'],
    pad: ['single', 'summary'],
    elevation: ['single', 'summary'],
    company: ['summary'],
    group: ['single', 'summary'],
    kns: ['single', 'summary'],
    well: ['single'],
    shaft: ['single', 'summary'],
};

export const getPropertiesFromObject = (obj: Record<string, unknown>): number[] => Object.keys(obj).map(Number);

export const excludeNonExistentIDs = (arr1: number[], arr2: number[]): number[] => arr1.filter(id => arr2.includes(id));

export const handleResetCache = async () => {
    try {
        localStorage.clear();
        await fetch('/api/clear-site-data');

        location.reload();
    } catch (error) {
        console.warn('Clear-Site-Data failure: ', error);
    }
};

export const handleResetCookie = async () => {
    document.cookie.split(';').forEach(cookie => {
        document.cookie = cookie.replace(/^ +/, '').replace(/=.*/, '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/');
    });
    document.cookie = 'cookieName=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; HttpOnly';
    try {
        await fetch('/api/clear-cookie');
        location.reload();
        localStorage.setItem('showResetMessage', 'true');
    } catch (error) {
        console.warn('Clear-Cookie failure: ', error);
    }
};

export const getFilteredVersions = (keys: any[], versions: any, activeItemKey: any, newCustomGeoVersion: any) => {
    if ((activeItemKey && versions && Object.keys(versions).length > 0) || versions?.length > 0) {
        let clonedVersions = activeItemKey ? { ...versions } : [...versions];
        const checkedKeys = Object.values(keys).filter(key => key.checked);

        if (checkedKeys.length === Object.values(keys).length) {
            if (
                activeItemKey &&
                newCustomGeoVersion &&
                clonedVersions &&
                clonedVersions[activeItemKey] &&
                Array.isArray(clonedVersions[activeItemKey])
            )
                clonedVersions[activeItemKey] = [newCustomGeoVersion, ...clonedVersions[activeItemKey]];
            return clonedVersions;
        }

        if (checkedKeys.length === 0) {
            if (activeItemKey) clonedVersions[activeItemKey] = [];
            else clonedVersions = [];
            return clonedVersions;
        }

        const shouldIncludeVersion = (version: any) => {
            const systemKey = Object.values(keys).find(k => k.itemKey === 'system');
            if (systemKey?.checked && !version.isCustom) return true;

            if (!version.isCustom) return false;

            const commonKey = Object.values(keys).find(k => k.itemKey === 'common');
            const privateKey = Object.values(keys).find(k => k.itemKey === 'isPrivate');
            const readOnlyKey = Object.values(keys).find(k => k.itemKey === 'readOnly');

            const isCommon = !version.isPrivate && !version.readOnly;
            const isPrivateOnly = version.isPrivate && !version.readOnly;
            const isReadOnlyOnly = !version.isPrivate && version.readOnly;

            if (commonKey?.checked && isCommon) return true;
            if (privateKey?.checked && isPrivateOnly) return true;
            if (readOnlyKey?.checked && isReadOnlyOnly) return true;

            return false;
        };

        if (activeItemKey) {
            clonedVersions[activeItemKey] = clonedVersions[activeItemKey].filter(shouldIncludeVersion);
        } else {
            clonedVersions = clonedVersions.filter(shouldIncludeVersion);
        }

        if (activeItemKey && newCustomGeoVersion && clonedVersions[activeItemKey].length > 0)
            clonedVersions[activeItemKey] = [newCustomGeoVersion, ...clonedVersions[activeItemKey]];

        return clonedVersions;
    }
};

export const getValue = (minDepth: number, scale: number) => (value: number) => {
    return minDepth + value / scale - 1;
};
